import { collection, getDocs, addDoc, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../../../firebase/config';

export type Location = {
    id: string;
    name: string;
    headOfficeAddress?: string;
    /** Region anchor from Locations page / Firestore (same shape as branch geo). */
    location?: { lat?: number | null; long?: number | null } | null;
};

export type Bank = {
    id: string;
    name?: string;
    /** If set on the bank document, branch forms can show client code as read-only. */
    defaultClientCode?: string;
};

export type Manager = {
    id: string;
    name: string;
    email?: string;
    mobile?: string;
    type?: string;
};

export const BRANCH_TYPE_OPTIONS = [
    'Branch',
    'CPC',
    'Booth',
    'BCP',
    'Office',
    'Call Center',
    'Konnect',
    'Other',
] as const;

export const COVERED_AREA_OPTIONS = [
    '',
    'Ground Only',
    'Ground + Basement',
    'Ground + First Floor',
    'Ground + Basement + First Floor',
    'Multi Story',
] as const;

/** SRA: Green safest → Yellow safe → Orange might be danger → Red danger */
export const SRA_LEVEL_OPTIONS = [
    { value: 'green' as const, label: 'Green', hint: 'Safest', dotClass: 'bg-emerald-500' },
    { value: 'yellow' as const, label: 'Yellow', hint: 'Safe', dotClass: 'bg-yellow-400' },
    { value: 'orange' as const, label: 'Orange', hint: 'Might be danger', dotClass: 'bg-orange-500' },
    { value: 'red' as const, label: 'Red', hint: 'Danger', dotClass: 'bg-red-600' },
];

export type SraLevel = 'green' | 'yellow' | 'orange' | 'red';

export type BranchGeoJson = {
    lat: number | null;
    long: number | null;
    distanceFromRegion?: string;
};

export type BranchFormData = {
    branchCode: string;
    address: string;
    mobile: string;
    locationId: string;
    securityCode: string;
    psoId?: string;
    branchManagerId?: string;
    operationalManagerId?: string;
    areaManagerId?: string;
    customLocationName?: string;
    branchType: string;
    branchTypeOther: string;
    cashLimitMillion: string;
    atmAvailable: boolean;
    atmMountType: 'wall_mounted' | 'standalone' | '';
    atmSeparateFromMainEntrance: boolean;
    vantagePointAvailable: boolean;
    emergencyExitAvailable: boolean;
    lockerAvailable: boolean;
    coveredArea: string;
    landmark: string;
    nearestOtherBanksBranches: string;
    /** Filled from Google Places when lat/long is set (editable). */
    nearestPoliceStationName: string;
    nearestPoliceStationAddress: string;
    nearestPoliceStationPhone: string;
    latitude: string;
    longitude: string;
    distanceFromRegion: string;
    sraLevel: SraLevel;
    sraReason: string;
    eventAffectedEnabled: boolean;
    eventAffectedAreasNote: string;
    saturdayOpen: boolean;
};

export const defaultExtendedBranchForm = (): Pick<
    BranchFormData,
    | 'branchType'
    | 'branchTypeOther'
    | 'cashLimitMillion'
    | 'atmAvailable'
    | 'atmMountType'
    | 'atmSeparateFromMainEntrance'
    | 'vantagePointAvailable'
    | 'emergencyExitAvailable'
    | 'lockerAvailable'
    | 'coveredArea'
    | 'landmark'
    | 'nearestOtherBanksBranches'
    | 'nearestPoliceStationName'
    | 'nearestPoliceStationAddress'
    | 'nearestPoliceStationPhone'
    | 'latitude'
    | 'longitude'
    | 'distanceFromRegion'
    | 'sraLevel'
    | 'sraReason'
    | 'eventAffectedEnabled'
    | 'eventAffectedAreasNote'
    | 'saturdayOpen'
> => ({
    branchType: 'Branch',
    branchTypeOther: '',
    cashLimitMillion: '',
    atmAvailable: true,
    atmMountType: '',
    atmSeparateFromMainEntrance: true,
    vantagePointAvailable: true,
    emergencyExitAvailable: true,
    lockerAvailable: true,
    coveredArea: '',
    landmark: '',
    nearestOtherBanksBranches: '',
    nearestPoliceStationName: '',
    nearestPoliceStationAddress: '',
    nearestPoliceStationPhone: '',
    latitude: '',
    longitude: '',
    distanceFromRegion: '',
    sraLevel: 'green',
    sraReason: '',
    eventAffectedEnabled: false,
    eventAffectedAreasNote: '',
    saturdayOpen: false,
});

export function parseGeoForSubmit(formData: BranchFormData): BranchGeoJson {
    const lat = parseFloat(String(formData.latitude).replace(/,/g, '').trim());
    const lon = parseFloat(String(formData.longitude).replace(/,/g, '').trim());
    const geo: BranchGeoJson = {
        lat: !Number.isNaN(lat) && String(formData.latitude).trim() !== '' ? lat : null,
        long: !Number.isNaN(lon) && String(formData.longitude).trim() !== '' ? lon : null,
    };
    if (formData.distanceFromRegion.trim()) {
        geo.distanceFromRegion = formData.distanceFromRegion.trim();
    }
    return geo;
}

export async function fetchLocations(): Promise<Location[]> {
    const locSnap = await getDocs(collection(db, 'locations'));
    return locSnap.docs.map(d => ({ id: d.id, ...d.data() } as Location));
}

export async function fetchBankInfo(uid: string): Promise<Bank | null> {
    const bankDoc = await getDoc(doc(db, 'banks', uid));
    if (bankDoc.exists()) {
        const bankData = bankDoc.data();
        const defaultClientCode = [bankData.clientCode, bankData.defaultClientCode, bankData.securityCode]
            .find((v) => typeof v === 'string' && String(v).trim()) as string | undefined;
        return {
            id: uid,
            name: bankData.companyName || bankData.name || 'My Bank',
            defaultClientCode: defaultClientCode ? String(defaultClientCode).trim() : undefined,
        };
    }
    return {
        id: uid,
        name: 'My Bank',
    };
}

export async function fetchManagers(bankId: string): Promise<Manager[]> {
    const mgrSnap = await getDocs(collection(db, 'banks', bankId, 'managers'));
    return mgrSnap.docs.map(d => ({
        id: d.id,
        ...d.data()
    } as Manager));
}

export async function submitBranch(bankId: string, formData: BranchFormData): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid || !bankId) {
        throw new Error('User not authenticated or bank ID missing');
    }

    let finalLocationId = formData.locationId;

    if (formData.locationId === 'custom' && formData.customLocationName) {
        const locRef = await addDoc(collection(db, 'locations'), {
            name: formData.customLocationName.trim()
        });
        finalLocationId = locRef.id;
    }

    const branchData: Record<string, unknown> = {
        branchCode: formData.branchCode.trim(),
        address: formData.address.trim(),
        mobile: formData.mobile.trim(),
        locationId: finalLocationId,
        securityCode: formData.securityCode.trim(),
        psoId: formData.psoId || uid,
        isActive: true,
        createdAt: new Date(),
        branchType: formData.branchType?.trim() || 'Branch',
        atmAvailable: !!formData.atmAvailable,
        vantagePointAvailable: !!formData.vantagePointAvailable,
        emergencyExitAvailable: !!formData.emergencyExitAvailable,
        lockerAvailable: !!formData.lockerAvailable,
        saturdayOpen: !!formData.saturdayOpen,
        location: parseGeoForSubmit(formData),
        eventAffectedActive: !!formData.eventAffectedEnabled,
    };

    if (formData.branchType === 'Other' && formData.branchTypeOther.trim()) {
        branchData.branchTypeOther = formData.branchTypeOther.trim();
    }

    const cash = parseFloat(String(formData.cashLimitMillion).replace(/,/g, ''));
    if (!Number.isNaN(cash) && String(formData.cashLimitMillion).trim() !== '') {
        branchData.cashLimitMillion = cash;
    }

    if (formData.atmAvailable) {
        branchData.atmMountType = formData.atmMountType || null;
        branchData.atmSeparateFromMainEntrance = !!formData.atmSeparateFromMainEntrance;
    } else {
        branchData.atmMountType = null;
        branchData.atmSeparateFromMainEntrance = null;
    }

    if (formData.coveredArea.trim()) {
        branchData.coveredArea = formData.coveredArea.trim();
    }
    if (formData.landmark.trim()) {
        branchData.landmark = formData.landmark.trim();
    }
    if (formData.nearestOtherBanksBranches.trim()) {
        branchData.nearestOtherBanksBranches = formData.nearestOtherBanksBranches.trim();
    }
    if (formData.nearestPoliceStationName.trim()) {
        branchData.nearestPoliceStationName = formData.nearestPoliceStationName.trim();
    }
    if (formData.nearestPoliceStationAddress.trim()) {
        branchData.nearestPoliceStationAddress = formData.nearestPoliceStationAddress.trim();
    }
    if (formData.nearestPoliceStationPhone.trim()) {
        branchData.nearestPoliceStationPhone = formData.nearestPoliceStationPhone.trim();
    }

    branchData.sraLevel = formData.sraLevel || 'green';
    if (formData.sraLevel === 'orange' || formData.sraLevel === 'red') {
        branchData.sraReason = formData.sraReason.trim() || null;
    } else {
        branchData.sraReason = null;
    }

    if (formData.eventAffectedEnabled && formData.eventAffectedAreasNote.trim()) {
        branchData.eventAffectedAreasNote = formData.eventAffectedAreasNote.trim();
    } else {
        branchData.eventAffectedAreasNote = null;
    }

    if (formData.branchManagerId) {
        branchData.branchManagerId = formData.branchManagerId;
    }
    if (formData.operationalManagerId) {
        branchData.operationalManagerId = formData.operationalManagerId;
    }
    if (formData.areaManagerId) {
        branchData.areaManagerId = formData.areaManagerId;
    }

    await addDoc(collection(db, 'banks', bankId, 'branch'), branchData as any);
}
